import type { SupplierWorkforce } from '@tandemone/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useReducer } from 'react'

import { extractErrorMessage } from '@/api/extract-error'
import { useDirtyVsInitial } from '@/domains/companies'

import { updateSupplierWorkforce } from './api'
import { invalidateOnboarding } from './invalidate-onboarding'
import {
  type WorkforceDerived,
  type WorkforceFieldKey,
  type WorkforceFormAction,
  type WorkforceFormState,
  WorkforceFormContext
} from './workforce-context'

const EMPTY: Record<WorkforceFieldKey, string> = {
  totalEmployees: '',
  corporateStaff: '',
  engineeringEngineers: '',
  engineeringTechnicians: '',
  productionEngineers: '',
  productionTechnicians: '',
  qaEngineers: '',
  qaTechnicians: ''
}

function reducer(
  state: WorkforceFormState,
  action: WorkforceFormAction
): WorkforceFormState {
  switch (action.type) {
    case 'setField':
      return { ...state, [action.field]: action.value.replace(/\D/g, '') }
    case 'submitError':
      return { ...state, submitError: action.message }
    default:
      return state
  }
}

function initialState(server: SupplierWorkforce | null): WorkforceFormState {
  if (!server) return { ...EMPTY, submitError: null }
  return {
    totalEmployees: String(server.totalEmployees),
    corporateStaff: String(server.corporateStaff),
    engineeringEngineers: String(server.engineeringEngineers),
    engineeringTechnicians: String(server.engineeringTechnicians),
    productionEngineers: String(server.productionEngineers),
    productionTechnicians: String(server.productionTechnicians),
    qaEngineers: String(server.qaEngineers),
    qaTechnicians: String(server.qaTechnicians),
    submitError: null
  }
}

function derive(state: WorkforceFormState): WorkforceDerived {
  const total =
    state.totalEmployees === '' ? Number.NaN : Number(state.totalEmployees)
  const totalMissing = !Number.isFinite(total)
  return { totalMissing, isValid: !totalMissing && total >= 0 }
}

export function WorkforceFormProvider({
  children,
  initial,
  onSuccess
}: {
  children: ReactNode
  initial: SupplierWorkforce | null
  onSuccess: () => void
}) {
  const queryClient = useQueryClient()
  const [state, dispatch] = useReducer(reducer, initialState(initial))
  /**
   * register dirty form so the company switcher prompts before
   * re-scoping a half-edited workforce step. submitError is transient
   * server-side feedback, not user input.
   */
  useDirtyVsInitial('workforce-form', state, ['submitError'])

  const mutation = useMutation({
    mutationFn: (body: SupplierWorkforce) => updateSupplierWorkforce(body),
    onSuccess: async () => {
      await invalidateOnboarding(queryClient, 'supplier-workforce')
      onSuccess()
    },
    onError: failure =>
      dispatch({ type: 'submitError', message: extractErrorMessage(failure) })
  })

  const derived = derive(state)

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!derived.isValid || mutation.isPending) return
    dispatch({ type: 'submitError', message: null })
    mutation.mutate({
      totalEmployees: parseIntOrZero(state.totalEmployees),
      corporateStaff: parseIntOrZero(state.corporateStaff),
      engineeringEngineers: parseIntOrZero(state.engineeringEngineers),
      engineeringTechnicians: parseIntOrZero(state.engineeringTechnicians),
      productionEngineers: parseIntOrZero(state.productionEngineers),
      productionTechnicians: parseIntOrZero(state.productionTechnicians),
      qaEngineers: parseIntOrZero(state.qaEngineers),
      qaTechnicians: parseIntOrZero(state.qaTechnicians)
    })
  }

  return (
    <WorkforceFormContext.Provider
      value={{
        state,
        derived,
        dispatch,
        isSubmitting: mutation.isPending,
        handleSubmit
      }}
    >
      {children}
    </WorkforceFormContext.Provider>
  )
}

function parseIntOrZero(s: string): number {
  return s === '' ? 0 : Number(s)
}

export const WORKFORCE_MATRIX: ReadonlyArray<{
  label: string
  engineersKey: WorkforceFieldKey
  engineersDescription: string
  techniciansKey: WorkforceFieldKey
  techniciansDescription: string
}> = [
  {
    label: 'Engineering staff',
    engineersKey: 'engineeringEngineers',
    engineersDescription: 'Design, process, tooling engineering',
    techniciansKey: 'engineeringTechnicians',
    techniciansDescription: 'CAD/CAM, CNC programming, build support'
  },
  {
    label: 'Production staff',
    engineersKey: 'productionEngineers',
    engineersDescription:
      'Manufacturing/process engineers: routings, cycle time, layout',
    techniciansKey: 'productionTechnicians',
    techniciansDescription: 'Operators, setup, assembly, maintenance'
  },
  {
    label: 'Quality assurance staff',
    engineersKey: 'qaEngineers',
    engineersDescription: 'QEs/SQEs: APQP, PPAP, audits, corrective action',
    techniciansKey: 'qaTechnicians',
    techniciansDescription: 'Inspectors, CMM, lab'
  }
]
